import { memo, useCallback, useMemo, useState } from 'react';
import { Button, Dropdown, SegmentedControl } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';
import { Plus } from 'lucide-react';

import { ASSIGNABLE_USER_TYPES, REQUIRE_ROLE_FOR_MEMBER } from '../../../constants/accessRules.js';
import { groupItems, roleItems, toggleId } from './accessLists.js';
import CatalogChecklist from './CatalogChecklist.jsx';

const EMPTY_ACCESS = Object.freeze({ role_ids: [], group_ids: [] });

/**
 * `+ Role` popover of a row: Member/Admin toggle, role search with studio subtitles, No
 * access and More options (groups). Every change is applied to the row immediately.
 *
 * @param {object} props
 * @param {{ rowNumber: number, person: Record<string, string>, access: object|null }} props.row
 * @param {Array<{ id: string, name: string, studios: string[] }>} props.roles
 * @param {Array<{ id: string, name: string }>} props.groups
 * @param {(rowNumber: number, access: object|null) => void} props.onChange
 */
function AccessPopover({ row, roles, groups, onChange }) {
  const { t } = useTranslation('people-access');
  const { rowNumber, access } = row;
  // Read from the row, so bulk assign, apply from file and Undo keep the toggle in step.
  const userType = access?.user_type ?? ASSIGNABLE_USER_TYPES[0];
  const [showMore, setShowMore] = useState(false);
  const roleOptions = useMemo(() => roleItems(roles), [roles]);
  const groupOptions = useMemo(() => groupItems(groups), [groups]);
  const userTypes = useMemo(
    () => ASSIGNABLE_USER_TYPES.map((value) => ({ value, label: t(`terms.userType.${value}`) })),
    [t],
  );
  const current = access ?? EMPTY_ACCESS;
  // A Member without a role has no access (groups alone grant nothing), so a group ticked first
  // would be lost together with the whole access.
  const groupsNeedRole = REQUIRE_ROLE_FOR_MEMBER && userType === 'member' && current.role_ids.length === 0;

  const apply = useCallback(
    (change) => onChange(rowNumber, { user_type: userType, ...current, ...change }),
    [onChange, rowNumber, userType, current],
  );
  const handleUserType = useCallback(
    (next) => {
      onChange(rowNumber, { ...current, user_type: next });
    },
    [onChange, rowNumber, current],
  );
  const handleRole = useCallback((id) => apply({ role_ids: toggleId(current.role_ids, id) }), [apply, current.role_ids]);
  const handleGroup = useCallback((id) => apply({ group_ids: toggleId(current.group_ids, id) }), [apply, current.group_ids]);
  const handleNoAccess = useCallback(() => onChange(rowNumber, null), [onChange, rowNumber]);
  const handleMore = useCallback(() => setShowMore((open) => !open), []);

  const name = `${row.person.first_name} ${row.person.last_name}`.trim() || row.person.email;

  return (
    <Dropdown
      variant="ghost"
      size="small"
      usePortal
      width="20rem"
      menuMaxHeight="none"
      trigger={
        <Button variant="ghost" size="sm" leftIcon={Plus} aria-label={`${t('import.review.access.addRole')} ${name}`}>
          {t('import.review.access.addRole')}
        </Button>
      }
    >
      <div className="flex flex-col gap-3 p-3">
        <p className="text-sm font-semibold text-content">{t('import.review.popover.title', { name })}</p>
        <div className="w-fit">
          <SegmentedControl
            value={userType}
            options={userTypes}
            onChange={handleUserType}
            ariaLabel={t('import.review.popover.userType')}
          />
        </div>
        <CatalogChecklist
          items={roleOptions}
          selectedIds={current.role_ids}
          onToggle={handleRole}
          searchLabel={t('import.review.popover.rolesSearch')}
          showCount
        />
        {showMore && !groupsNeedRole ? (
          <CatalogChecklist
            items={groupOptions}
            selectedIds={current.group_ids}
            onToggle={handleGroup}
            searchLabel={t('import.review.popover.groupsSearch')}
          />
        ) : null}
        {showMore && groupsNeedRole ? (
          <p className="text-xs text-content-secondary">{t('import.review.popover.groupsNeedRole')}</p>
        ) : null}
        <div className="flex items-center justify-between border-t border-edge pt-2">
          <Button variant="ghost" size="sm" disabled={!access} onClick={handleNoAccess}>
            {t('import.review.popover.noAccess')}
          </Button>
          <Button variant="ghost" size="sm" onClick={handleMore}>
            {showMore ? t('import.review.popover.lessOptions') : t('import.review.popover.moreOptions')}
          </Button>
        </div>
      </div>
    </Dropdown>
  );
}

export default memo(AccessPopover);
